/**
 * GSD – Imprint Product Grid
 *
 * Client-side imprint light-switch for a server-rendered product grid.
 *
 * STATE MODEL (per instance, independent of every other instance on the page).
 *
 * REWRITTEN 03 SEP 2026. The marks are now TWO INDEPENDENT ON/OFF SWITCHES, not
 * a mutually exclusive filter. The previous model treated "none on" and "both
 * on" as the same result, which made a mark's appearance ambiguous in the hand:
 * unselected and hovered looked identical, so you could not tell what a click
 * had done.
 *
 *   on = Set of imprint slugs currently switched ON. Starts with every slug the
 *        section rendered a mark for, so both imprints show on load.
 *
 *     both on  -> every card visible
 *     one on   -> only that imprint's cards visible
 *     none on  -> UNREACHABLE, see below
 *
 *   Clicking a mark toggles only that imprint. The two marks never affect each
 *   other.
 *
 * TURNING BOTH OFF IS REFUSED. Clicking the last remaining ON mark does nothing
 * at all - no state change, no re-render. An empty carousel is never a thing a
 * user meant to ask for, and the alternative (treating "none" as "everything",
 * as the old model did) is what made the marks ambiguous in the first place.
 *
 * The switches are driven by aria-pressed, which is now literally true: these
 * are toggle buttons, so pressed maps to on with no interpretation needed. The
 * CSS keys the off appearance off [aria-pressed='false'] and has no hover rules
 * at all, deliberately - colour vs mono means on vs off and nothing else, and it
 * therefore has to work identically on touch.
 *
 * UNSTAMPED PRODUCTS ARE NEVER HIDDEN. An item with no data-imprint attribute
 * has no imprint metafield behind it; it stays visible in every state. A product
 * added later without custom.imprint therefore keeps showing rather than
 * silently vanishing when someone clicks a mark.
 *
 * DEGRADATION. The server renders every card visible and every mark PRESSED,
 * which is now the honest starting state rather than a convenient one. This file
 * only ever ADDS the hiding class. If it fails to load, throws, or is blocked,
 * both marks read as on and the page is a normal full product grid - never an
 * empty one, and never one whose marks claim to be off while everything shows.
 */

const HIDDEN_CLASS = 'gsd-imprint-grid--hidden';

// Movement past this many pixels turns a press into a drag, which both starts
// scrolling and suppresses the click that would otherwise open a product.
const DRAG_THRESHOLD = 5;

// Applied to every SECOND VISIBLE card on mobile so a swipe pages by two rather
// than by one. The CSS default is that every card snaps; this class removes the
// snap, so a failed script degrades to per-card snapping rather than to none.
const NOSNAP_CLASS = 'gsd-imprint-grid__item--nosnap';
const PAIR_SNAP_QUERY = '(max-width: 749px)';

// Page dots. The buttons are built here rather than in Liquid because the page
// count is ceil(scrollWidth / clientWidth), which has no server-side existence.
const DOT_CLASS = 'gsd-imprint-grid__dot';
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

class GsdImprintGrid extends HTMLElement {
  connectedCallback() {
    // connectedCallback runs again when the theme editor re-renders the
    // section, so state is rebuilt from scratch rather than carried over.
    this.onMarkClick = this.onMarkClick.bind(this);

    this.marks = this.own('[data-gsd-imprint-mark]');

    // Seeded from the marks this section actually rendered, not from a hardcoded
    // pair, so a section configured with one imprint still starts consistent.
    this.on = new Set(
      this.marks.map((mark) => mark.dataset.imprint).filter(Boolean)
    );
    this.items = this.own('[data-gsd-imprint-item]');
    this.scroller = this.own('[data-gsd-imprint-scroller]')[0] || null;
    if (this.scroller) this.enableDragScroll();
    if (this.scroller) this.enableDots();

    // Bind whenever there are marks. Deliberately NOT gated on items: an empty
    // grid used to leave the marks unbound, so clicking did nothing and only the
    // CSS hover fired - which read as "the toggle is hover-driven". A grid with
    // no cards should still toggle; it just has nothing to filter.
    if (this.marks.length > 0) {
      this.marks.forEach((mark) => mark.addEventListener('click', this.onMarkClick));
    }

    /*
     * Media query LAST, and guarded. MediaQueryList.addEventListener needs
     * Safari 14+; on anything older this throws. Sitting earlier in this method
     * it would have taken the whole component down with it - no filtering, no
     * drag-scroll, no snapping - because connectedCallback would abort before
     * any of them were bound. Last and guarded, the worst case is that pair
     * snapping does not update on rotation.
     *
     * The early `return` that used to sit above this is gone too: it meant a
     * carousel with no imprint marks configured never reached apply(), so it got
     * no pair snapping either. Nothing here depends on marks existing.
     */
    try {
      this.pairSnapQuery = window.matchMedia(PAIR_SNAP_QUERY);
      if (typeof this.pairSnapQuery.addEventListener === 'function') {
        this.onPairSnapChange = () => this.updateSnapPoints();
        this.pairSnapQuery.addEventListener('change', this.onPairSnapChange);
      }
    } catch (error) {
      this.pairSnapQuery = null;
    }

    this.apply();
  }

  disconnectedCallback() {
    if (this.marks) {
      this.marks.forEach((mark) => mark.removeEventListener('click', this.onMarkClick));
    }
    this.disableDragScroll();
    this.disableDots();
    if (this.pairSnapQuery && this.onPairSnapChange) {
      this.pairSnapQuery.removeEventListener('change', this.onPairSnapChange);
    }
  }

  /**
   * Snap on every second VISIBLE card, so a mobile swipe lands on a pair
   * boundary. Driven off visibility rather than DOM position on purpose:
   * filtering hides cards with display:none, and :nth-child still counts hidden
   * elements, so a CSS-only rule would put the pairs on the wrong cards the
   * moment an imprint mark was clicked.
   */
  updateSnapPoints() {
    if (!this.scroller || !this.items) return;

    const paged = this.pairSnapQuery ? this.pairSnapQuery.matches : false;
    let visibleIndex = 0;

    this.items.forEach((item) => {
      if (item.classList.contains(HIDDEN_CLASS)) {
        // Never leave a stale class on a hidden card - it becomes wrong the
        // moment the filter changes and the card comes back.
        item.classList.remove(NOSNAP_CLASS);
        return;
      }
      item.classList.toggle(NOSNAP_CLASS, paged && visibleIndex % 2 === 1);
      visibleIndex += 1;
    });
  }

  /* ---------------------------------------------------------------------- *
   * Page dots
   *
   * DERIVED FROM SCROLL GEOMETRY, WITH NO INDEX STORED ANYWHERE. Count is
   * ceil(scrollWidth / clientWidth), active is round(scrollLeft / clientWidth),
   * a click scrolls to n * clientWidth. On this section that is not a
   * preference: the imprint filter hides cards at runtime, so any stored paging
   * state is wrong the instant a mark is clicked.
   *
   * Same controller as the hero's, written separately on purpose. The two are
   * NOT extracted to a shared file yet - the hero pages by whole slides and
   * this pages by viewport, and they should be free to diverge until it is
   * clear they genuinely agree.
   *
   * Additive, like everything else here: if this file fails to load the dot row
   * stays hidden and the row is still a scrollable list of every product.
   * ---------------------------------------------------------------------- */

  enableDots() {
    this.dotsRow = this.own('[data-gsd-imprint-dots]')[0] || null;
    if (!this.dotsRow) return;

    this.dots = [];
    this.dotsRaf = 0;

    // One frame at most. Three carousels recomputing on every scroll event is a
    // real cost on a phone.
    this.onDotsScroll = () => {
      if (this.dotsRaf) return;
      this.dotsRaf = requestAnimationFrame(() => {
        this.dotsRaf = 0;
        this.syncDots();
      });
    };

    // Page width IS clientWidth, so a resize changes the count, not just the
    // active dot.
    this.onDotsResize = () => this.updateDots();

    this.scroller.addEventListener('scroll', this.onDotsScroll, { passive: true });
    window.addEventListener('resize', this.onDotsResize);
  }

  disableDots() {
    if (!this.dotsRow) return;
    this.scroller.removeEventListener('scroll', this.onDotsScroll);
    window.removeEventListener('resize', this.onDotsResize);
    if (this.dotsRaf) cancelAnimationFrame(this.dotsRaf);
    this.dotsRaf = 0;
  }

  pageCount() {
    const width = this.scroller.clientWidth;
    // A zero width means the row is not laid out yet (display:none, or before
    // first layout). Report no pages rather than dividing by zero; the resize
    // handler and the next apply() both recover it.
    if (!width) return 0;
    return Math.ceil(this.scroller.scrollWidth / width);
  }

  activePage() {
    const width = this.scroller.clientWidth;
    return width ? Math.round(this.scroller.scrollLeft / width) : 0;
  }

  updateDots() {
    if (!this.dotsRow || !this.scroller) return;

    const count = this.pageCount();

    // Nothing to page through: hide the row rather than render a single dot
    // that does nothing when clicked.
    if (count <= 1) {
      this.dotsRow.hidden = true;
      this.dotsRow.replaceChildren();
      this.dots = [];
      return;
    }

    // Rebuild only when the count actually changed. replaceChildren drops the
    // old buttons and their listeners together, so nothing is left bound.
    if (this.dots.length !== count) {
      const dots = [];
      for (let i = 0; i < count; i += 1) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = DOT_CLASS;
        dot.setAttribute('aria-label', `Go to page ${i + 1} of ${count}`);
        dot.addEventListener('click', () => this.goToPage(i));
        dots.push(dot);
      }
      this.dots = dots;
      this.dotsRow.replaceChildren(...dots);
    }

    this.dotsRow.hidden = false;
    this.syncDots();
  }

  syncDots() {
    if (!this.dots || !this.dots.length) return;

    const active = this.activePage();
    this.dots.forEach((dot, index) => {
      // aria-current IS the state. The CSS keys off it, so there is no class to
      // fall out of step with the attribute.
      if (index === active) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  }

  goToPage(index) {
    let instant = false;
    try {
      instant = window.matchMedia(REDUCED_MOTION_QUERY).matches;
    } catch (error) {
      instant = false;
    }

    this.scroller.scrollTo({
      left: index * this.scroller.clientWidth,
      behavior: instant ? 'auto' : 'smooth',
    });
  }

  /* ---------------------------------------------------------------------- *
   * Drag-to-scroll
   *
   * The row scrolls natively on touch and trackpad because it is a plain
   * overflow container. A mouse user has neither gesture and, with the
   * scrollbar hidden, no affordance at all - so press-and-drag is added here.
   *
   * MOUSE ONLY, DELIBERATELY. `pointerdown` returns early for any pointerType
   * other than 'mouse'. Capturing a touch pointer would take the gesture away
   * from the browser and REPLACE native touch scrolling with a JS reimplementation
   * of it - momentum, rubber-banding and all. Leaving touch alone keeps the
   * native behaviour that already works.
   *
   * Everything is instance state on `this` and every listener is bound to this
   * instance's scroller, so multiple sections on a page never interact. No
   * globals, no document-level listeners.
   *
   * Degradation: this is additive. If the file fails to load, the row is still
   * a native overflow container and touch and trackpad still scroll it.
   * ---------------------------------------------------------------------- */

  enableDragScroll() {
    this.pointerId = null;
    this.dragged = false;
    this.startX = 0;
    this.startScrollLeft = 0;

    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerEnd = this.onPointerEnd.bind(this);
    this.onDragStart = this.onDragStart.bind(this);
    this.onClickCapture = this.onClickCapture.bind(this);

    this.scroller.addEventListener('pointerdown', this.onPointerDown);
    this.scroller.addEventListener('pointermove', this.onPointerMove);
    this.scroller.addEventListener('pointerup', this.onPointerEnd);
    this.scroller.addEventListener('pointercancel', this.onPointerEnd);
    this.scroller.addEventListener('dragstart', this.onDragStart);
    // Capture phase: the click must be stopped before it reaches the product
    // link, not after.
    this.scroller.addEventListener('click', this.onClickCapture, true);

    // Set from JS, not CSS, so the grab affordance only appears when dragging
    // is actually available.
    this.scroller.style.cursor = 'grab';
  }

  disableDragScroll() {
    if (!this.scroller || !this.onPointerDown) return;
    this.scroller.removeEventListener('pointerdown', this.onPointerDown);
    this.scroller.removeEventListener('pointermove', this.onPointerMove);
    this.scroller.removeEventListener('pointerup', this.onPointerEnd);
    this.scroller.removeEventListener('pointercancel', this.onPointerEnd);
    this.scroller.removeEventListener('dragstart', this.onDragStart);
    this.scroller.removeEventListener('click', this.onClickCapture, true);
    this.scroller.style.cursor = '';
  }

  onPointerDown(event) {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;

    this.pointerId = event.pointerId;
    // Reset here rather than on release: a drag that ends without a click
    // would otherwise leave the suppressor armed for the next real click.
    this.dragged = false;
    this.startX = event.clientX;
    this.startScrollLeft = this.scroller.scrollLeft;

    /*
     * NOTHING ELSE HAPPENS HERE, AND THAT IS THE FIX (03 Sep 2026).
     *
     * setPointerCapture used to be called on every mouse press. Capturing a
     * pointer retargets the events that follow to the capturing element, and the
     * `click` that a press-release generates is dispatched against that element
     * rather than the one under the cursor - so it never reached the card's
     * anchor and NO CARD IN EITHER CAROUSEL WAS CLICKABLE WITH A MOUSE from
     * 26 Aug (d6ef08f) until today. Touch was unaffected the whole time, because
     * this handler returns early for any pointerType other than 'mouse', which
     * is what made "works on my phone, not on my laptop" the confirming test.
     *
     * Capture is now taken in onPointerMove, only once movement passes
     * DRAG_THRESHOLD - i.e. only once it is a drag rather than a click. A plain
     * click never captures, so the click lands normally.
     *
     * The drag visuals moved with it for the same reason: a click should not
     * flash the grabbing cursor or suspend scroll-snap.
     */
  }

  onPointerMove(event) {
    if (this.pointerId === null || event.pointerId !== this.pointerId) return;

    const dx = event.clientX - this.startX;

    if (!this.dragged) {
      if (Math.abs(dx) <= DRAG_THRESHOLD) return;
      this.dragged = true;

      // Now it is a drag. Capture, so the pointer keeps reporting to the
      // scroller even if it leaves the element mid-gesture.
      this.scroller.setPointerCapture(event.pointerId);
      this.scroller.style.cursor = 'grabbing';
      this.scroller.style.userSelect = 'none';
      // scroll-snap fights every scrollLeft written mid-drag, yanking the row
      // back to the nearest snap point on each frame. Suspended for the drag and
      // restored on release, which is what makes it settle onto a card.
      this.scroller.style.scrollSnapType = 'none';
    }

    this.scroller.scrollLeft = this.startScrollLeft - dx;
  }

  onPointerEnd(event) {
    if (this.pointerId === null || event.pointerId !== this.pointerId) return;

    if (this.scroller.hasPointerCapture(event.pointerId)) {
      this.scroller.releasePointerCapture(event.pointerId);
    }

    this.pointerId = null;
    this.scroller.style.cursor = 'grab';
    this.scroller.style.userSelect = '';
    // Restoring snap lets the browser settle to the nearest card. No scripted
    // animation is involved, so there is nothing here for prefers-reduced-motion
    // to suppress - the drag is direct manipulation and the settle is the
    // browser's own, governed by the page's scroll-behavior.
    this.scroller.style.scrollSnapType = '';
    // `dragged` is intentionally left set - onClickCapture reads it next.
  }

  onDragStart(event) {
    // Stops the browser's native link/image drag from hijacking the gesture,
    // but only while a drag of ours is actually in progress.
    if (this.pointerId !== null) event.preventDefault();
  }

  onClickCapture(event) {
    if (!this.dragged) return;
    event.preventDefault();
    event.stopPropagation();
    this.dragged = false;
  }


  /**
   * Elements belonging to THIS instance. Guards against a nested grid's
   * marks or cards being captured by an ancestor instance.
   */
  own(selector) {
    return Array.from(this.querySelectorAll(selector)).filter(
      (el) => el.closest('gsd-imprint-grid') === this
    );
  }

  onMarkClick(event) {
    const mark = event.currentTarget;
    const slug = mark.dataset.imprint;
    if (!slug) return;

    if (this.on.has(slug)) {
      // Refuse to switch off the last one on. Returning before any state change
      // means aria-pressed stays true and the row stays as it was, so the click
      // is a genuine no-op rather than a flicker that undoes itself.
      if (this.on.size <= 1) return;
      this.on.delete(slug);
    } else {
      this.on.add(slug);
    }

    this.apply();
  }

  apply() {
    (this.items || []).forEach((item) => {
      const imprint = item.dataset.imprint;
      // No imprint stamped -> never hidden, in any state.
      const hide = Boolean(imprint) && !this.on.has(imprint);
      item.classList.toggle(HIDDEN_CLASS, hide);
    });

    this.marks.forEach((mark) => {
      mark.setAttribute('aria-pressed', this.on.has(mark.dataset.imprint) ? 'true' : 'false');
    });

    // Visibility just changed, so the pair boundaries have moved.
    this.updateSnapPoints();

    // And so has the page count: hiding an imprint shrinks scrollWidth. This is
    // the one call site that covers both first render and every filter click.
    this.updateDots();
  }
}

if (!customElements.get('gsd-imprint-grid')) {
  customElements.define('gsd-imprint-grid', GsdImprintGrid);
}
